import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { formatEuro } from "@/lib/catalog-format";
import { toast } from "sonner";
import {
  Camera,
  Cable,
  MapPin,
  Plug,
  HardDrive,
  Server,
  Trash2,
  Search,
  Eraser,
  Pencil,
  Calculator,
  MousePointer,
} from "lucide-react";
// Leaflet CSS is dynamisch geladen samen met de Leaflet-module in de
// useEffect hieronder, zodat het niet in de kritieke CSS-bundle van
// andere routes terechtkomt (elimineert render-blocking op de home).

const SITE_URL = "https://elvion-group.nl";

const FAQS = [
  {
    q: "Hoeveel megapixel heb ik nodig voor een herkenbaar beeld?",
    a: "Vuistregel: 2MP (1080p) volstaat voor algemeen overzicht tot ±10 meter. 4MP geeft duidelijke gezichtsherkenning tot ±15 meter en kentekenherkenning op een oprit. 8MP/4K levert forensisch detail op grotere afstand (parkeerterrein, gevel) en laat je inzoomen op opgenomen beeld zonder kwaliteitsverlies.",
  },
  {
    q: "Welke resolutie kies ik voor welke locatie?",
    a: "Voordeur en hal: 2–4MP. Oprit met kentekens: 4–8MP. Parkeerterrein, gevel of opslaglocatie: 6–8MP (4K). Grote ruimtes met PTZ: 4MP met optische zoom levert vaak meer praktisch detail dan 8MP zonder zoom.",
  },
  {
    q: "Hoe weet ik hoeveel kabel ik nodig heb?",
    a: "Teken in de configurator op de kadasterkaart (op schaal) het kabeltracé van iedere camera naar je NVR-locatie. Wij tellen automatisch de meters op en rekenen standaard 25 meter extra reserve voor bochten, hoogteverschil en eindafwerking.",
  },
  {
    q: "Waarom 3 RJ45-connectoren per camera?",
    a: "Per camera reken je in de praktijk met 3 RJ45/keystone connectoren: één aan de camerazijde, één aan de patchzijde (NVR/switch) en één reserve voor hermontage of een verkeerd krimpen. Zo voorkom je tijdverlies tijdens installatie.",
  },
  {
    q: "Heb ik per camera een montagebeugel nodig?",
    a: "Dome- en turret-camera's worden meestal direct op een vlakke ondergrond gemonteerd. Voor muurmontage onder een dakgoot, op een hoek of paal raden wij een speciale beugel aan; deze configurator rekent standaard 1 montagebeugel per camera, je kunt dit per situatie doorgeven in je offerteaanvraag.",
  },
  {
    q: "Is één NVR genoeg voor mijn camera's?",
    a: "Een 4-kanaals NVR is geschikt voor 1–4 camera's, 8-kanaals voor 5–8 en 16-kanaals voor 9–16. De configurator selecteert automatisch het juiste model en combineert dit met een passende surveillance-grade harde schijf via onze HD-berekening.",
  },
  {
    q: "Welke harde schijf hoort bij mijn camerasysteem?",
    a: "We gebruiken dezelfde berekening als de HD Calculator: aantal camera's × bitrate × bewaardagen. Een 4-cam 4MP-systeem met 14 dagen bewaartermijn komt uit op ±4TB; bij 8 camera's en 4K-resolutie reken je al snel op 8–10TB. We adviseren altijd WD Purple of Seagate SkyHawk.",
  },
  {
    q: "Mag ik beelden onbeperkt bewaren?",
    a: "Nee. Onder de AVG geldt voor reguliere camerabewaking een richtlijn van maximaal 4 weken (28 dagen). Bij een geregistreerd incident mogen relevante beelden langer bewaard worden tot het is afgehandeld.",
  },
  {
    q: "Waarom een kadasterkaart in de configurator?",
    a: "De BRT-Achtergrondkaart van het Kadaster (PDOK) is een officiële topografische kaart op schaal. Hierdoor zijn de kabelafstanden die je tekent realistisch — geen schatting op een vage luchtfoto, maar metingen op een betrouwbare ondergrond.",
  },
  {
    q: "Kunnen jullie ook komen installeren?",
    a: "Ja. Onze monteurs installeren in heel Nederland. Geef in de offerte aan dat je installatie wenst, dan koppelen we de configuratie aan een passende afspraak en leveren we het systeem voorgemonteerd af.",
  },
];

// HD-berekening (gelijk aan /hd-configurator)
const BITRATE_MBPS: Record<string, number> = {
  "2": 2,
  "4": 4,
  "6": 6,
  "8": 8,
};

type HddOption = { slug: string; name: string; capacityTb: number; priceCents: number; inStock: boolean };
const HDDS: HddOption[] = [
  { slug: "wd-purple-2tb", name: "WD Purple 2TB Surveillance HDD", capacityTb: 2, priceCents: 18900, inStock: false },
  { slug: "wd-purple-4tb", name: "WD Purple 4TB Surveillance HDD", capacityTb: 4, priceCents: 24900, inStock: false },
  { slug: "seagate-skyhawk-6tb", name: "Seagate SkyHawk 6TB Surveillance HDD", capacityTb: 6, priceCents: 23900, inStock: false },
  { slug: "wd-purple-8tb", name: "WD Purple 8TB Surveillance HDD", capacityTb: 8, priceCents: 49000, inStock: false },
  { slug: "seagate-skyhawk-10tb", name: "Seagate SkyHawk 10TB Surveillance HDD", capacityTb: 10, priceCents: 47600, inStock: false },
  { slug: "wd-purple-pro-12tb", name: "WD Purple Pro 12TB Surveillance HDD", capacityTb: 12, priceCents: 51000, inStock: false },
];

type CamOption = {
  slug: string;
  name: string;
  mp: "2" | "4" | "6" | "8";
  priceCents: number;
};
const CAMERAS: CamOption[] = [
  {
    slug: "hikvision-ds-2cd1023g0e-i-2mp",
    name: "Hikvision 2MP Bullet IP-camera",
    mp: "2",
    priceCents: 7900,
  },
  {
    slug: "hikvision-ds-2cd1143g0-i-dome-4mp",
    name: "Hikvision DS-2CD1143G0-I 4MP Dome",
    mp: "4",
    priceCents: 11900,
  },
  {
    slug: "dahua-ipc-hdw2641t-zs-6mp",
    name: "Dahua IPC-HDW2641T-ZS 6MP Turret",
    mp: "6",
    priceCents: 16900,
  },
  {
    slug: "dahua-hdbw2841e-s-dome-8mp",
    name: "Dahua HDBW2841E-S 8MP Dome (4K)",
    mp: "8",
    priceCents: 21900,
  },
];

// Accessoires
const BRACKET = { slug: "muurbeugel-camera-universeel", name: "Muurbeugel IP-camera (universeel)", priceCents: 1490 };
const RJ45 = { slug: "keystone-cat6a-shielded-10pack", name: "Keystone CAT6a Shielded | 10-pack", priceCents: 2499, perPack: 10 };
const CABLE = { slug: "cat6a-utp-kabel-per-meter", name: "CAT6a U/UTP netwerkkabel (per meter)", priceCents: 95 };

function nvrFor(numCams: number) {
  if (numCams <= 4) return { slug: "hikvision-nvr-4ch", name: "Hikvision 4-kanaals NVR (PoE)", priceCents: 16900 };
  if (numCams <= 8) return { slug: "hikvision-nvr-8ch", name: "Hikvision 8-kanaals NVR (PoE)", priceCents: 22900 };
  if (numCams <= 16) return { slug: "hikvision-nvr-16ch", name: "Hikvision 16-kanaals NVR (PoE)", priceCents: 34900 };
  return { slug: "hikvision-nvr-32ch", name: "Hikvision 32-kanaals NVR (PoE)", priceCents: 54900 };
}

// Haversine afstand tussen twee lat/lng punten in meters
function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

type BomLine = { name: string; qty: string; priceCents: number; lineCents: number };
type BomResult = {
  numCams: number;
  cableMeters: number;
  drawnMeters: number;
  rj45Packs: number;
  nvr: ReturnType<typeof nvrFor>;
  hdd: { recommended: HddOption; neededTb: number };
  cam: CamOption;
  lines: BomLine[];
  totalCents: number;
};

function computeBom(input: {
  cams: { id: string; lat: number; lng: number }[];
  lines: { id: string; points: { lat: number; lng: number }[] }[];
  selectedCamSlug: string;
  days: number;
  bufferPct: number;
}): BomResult {
  const cam =
    CAMERAS.find((c) => c.slug === input.selectedCamSlug) ?? CAMERAS[1];
  const numCams = input.cams.length;
  let drawn = 0;
  for (const line of input.lines) {
    for (let i = 1; i < line.points.length; i++) {
      drawn += haversineMeters(line.points[i - 1], line.points[i]);
    }
  }
  const cableMeters = Math.max(0, Math.round(drawn + (drawn > 0 ? 25 : 0)));
  const rj45Packs = Math.ceil((numCams * 3) / RJ45.perPack);
  const nvr = nvrFor(Math.max(1, numCams));
  const cnt = Math.max(1, numCams);
  const bitrate = BITRATE_MBPS[cam.mp] ?? 4;
  const gbPerCamPerDay = (bitrate * 86400) / 8 / 1024;
  const totalGb = gbPerCamPerDay * cnt * input.days * (1 + input.bufferPct / 100);
  const neededTb = totalGb / 1024;
  const recommended =
    HDDS.find((h) => h.capacityTb >= neededTb) ?? HDDS[HDDS.length - 1];

  const lines: BomLine[] = [];
  if (numCams > 0) {
    lines.push({
      name: cam.name,
      qty: `${numCams}×`,
      priceCents: cam.priceCents,
      lineCents: numCams * cam.priceCents,
    });
    lines.push({
      name: BRACKET.name,
      qty: `${numCams}×`,
      priceCents: BRACKET.priceCents,
      lineCents: numCams * BRACKET.priceCents,
    });
    if (cableMeters > 0) {
      lines.push({
        name: `${CABLE.name} (${Math.round(drawn)}m + 25m reserve)`,
        qty: `${cableMeters} m`,
        priceCents: CABLE.priceCents,
        lineCents: cableMeters * CABLE.priceCents,
      });
    }
    if (rj45Packs > 0) {
      lines.push({
        name: RJ45.name,
        qty: `${rj45Packs} pak`,
        priceCents: RJ45.priceCents,
        lineCents: rj45Packs * RJ45.priceCents,
      });
    }
    lines.push({
      name: nvr.name,
      qty: "1×",
      priceCents: nvr.priceCents,
      lineCents: nvr.priceCents,
    });
    lines.push({
      name: `${recommended.name} (${neededTb.toFixed(2)} TB nodig)`,
      qty: "1×",
      priceCents: recommended.priceCents,
      lineCents: recommended.priceCents,
    });
  }
  const totalCents = lines.reduce((s, l) => s + l.lineCents, 0);
  return {
    numCams,
    cableMeters,
    drawnMeters: drawn,
    rj45Packs,
    nvr,
    hdd: { recommended, neededTb },
    cam,
    lines,
    totalCents,
  };
}

function formatEuroLocal(cents: number) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

function buildOrderPdfHtml(meta: {
  projectNumber: string;
  customerName: string;
  projectDate: string;
  address: string | null;
  days: number;
  bufferPct: number;
  logoUrl?: string;
  origin?: string;
}, bom: BomResult): string {
  const rows = bom.lines
    .map(
      (l) => `
      <tr>
        <td>${escapeHtml(l.name)}</td>
        <td class="num">${escapeHtml(l.qty)}</td>
        <td class="num">${formatEuroLocal(l.priceCents)}</td>
        <td class="num strong">${formatEuroLocal(l.lineCents)}</td>
      </tr>`,
    )
    .join("");
  const subtotal = bom.totalCents;
  const vat = Math.round(subtotal * 0.21);
  const total = subtotal + vat;
  const dateLabel = meta.projectDate
    ? new Date(meta.projectDate).toLocaleDateString("nl-NL")
    : "";
  const logoTag = meta.logoUrl
    ? `<img src="${escapeHtml(meta.logoUrl)}" alt="Elvion Group Nederland" class="logo-img" />`
    : `<div class="logo-text">Elvion <span>Group</span></div>`;
  const siteUrl = meta.origin ? meta.origin.replace(/^https?:\/\//, "") : "elvion-group.nl";

  return `<!doctype html>
<html lang="nl">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Bestellijst ${escapeHtml(meta.projectNumber || "")} — Elvion Group Nederland</title>
<style>
  /* ---- Brand tokens (synced with src/styles.css) ---- */
  :root {
    --brand: #0b2a4a;
    --orange: #f08a24;
    --orange-hover: #d9761a;
    --foreground: #1a2733;
    --text-secondary: #5a6b7b;
    --border: #e3e7eb;
    --muted: #f5f7f9;
    --background: #ffffff;
    --notes-bg: #fff6ec;
    --notes-border: #f6d0a1;
    --notes-fg: #6b3d10;
  }

  /* ---- Print page setup ---- */
  @page { size: A4; margin: 16mm 14mm 18mm; }
  @media print {
    .noprint { display: none !important; }
    body { padding: 0 !important; }
    table { page-break-inside: auto; }
    tr, .card, .notes, .foot { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
  }

  /* ---- Reset ---- */
  *, *::before, *::after { box-sizing: border-box; }
  html, body {
    background: var(--background);
    color: var(--foreground);
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
      "Helvetica Neue", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    font-size: 12px;
    line-height: 1.5;
    margin: 0;
    /* Preserve background colors when printing in every browser */
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    padding: 28px 32px 40px;
    max-width: 210mm;
    margin: 0 auto;
  }

  /* ---- Typography (mirrors website h1-h4 rules) ---- */
  h1, h2, h3, h4 { font-weight: 800; letter-spacing: -0.02em; margin: 0; color: var(--foreground); }
  h1 { font-size: 22px; line-height: 1.2; }
  h2 { font-size: 16px; line-height: 1.3; }
  h3 { font-size: 10.5px; line-height: 1.2; text-transform: uppercase; letter-spacing: 0.08em; color: var(--orange); font-weight: 700; }
  p  { margin: 0; }
  strong { font-weight: 700; color: var(--foreground); }

  /* ---- Header / brand bar ---- */
  .brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 3px solid var(--orange);
    padding-bottom: 14px;
    margin-bottom: 22px;
  }
  .logo-img { height: 36px; width: auto; display: block; }
  .logo-text { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; color: var(--brand); }
  .logo-text span { color: var(--orange); }
  .brand .meta { text-align: right; font-size: 10.5px; color: var(--text-secondary); }
  .badge {
    display: inline-block;
    background: var(--orange);
    color: #fff;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* ---- Intro ---- */
  .intro { margin-bottom: 18px; }
  .intro p { margin-top: 6px; color: var(--text-secondary); font-size: 12px; max-width: 64ch; }

  /* ---- Info cards grid ---- */
  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin: 16px 0 22px;
  }
  .card {
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 14px 16px;
    background: var(--background);
  }
  .card h3 { margin-bottom: 8px; }
  .card .row { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; font-size: 11.5px; }
  .card .row .k { color: var(--text-secondary); }
  .card .row .v { color: var(--foreground); font-weight: 600; text-align: right; }

  /* ---- Table ---- */
  .section-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 22px 0 8px;
  }
  .section-title .count { color: var(--text-secondary); font-size: 11px; }
  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    font-size: 11.5px;
  }
  thead th {
    background: var(--muted);
    color: var(--text-secondary);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
  }
  tbody td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    vertical-align: top;
    color: var(--foreground);
  }
  tbody tr:nth-child(even) td { background: color-mix(in oklab, var(--muted) 60%, white); }
  tbody tr:last-child td { border-bottom: none; }
  td.num, th.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  td.strong { font-weight: 700; }

  tfoot td {
    padding: 8px 12px;
    border: none;
    font-size: 12px;
  }
  tfoot .totals-label { text-align: right; color: var(--text-secondary); }
  tfoot .totals-value { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot tr.subtotal td { border-top: 1px solid var(--border); }
  tfoot tr.total td {
    border-top: 2px solid var(--brand);
    font-weight: 800;
    font-size: 14px;
    color: var(--brand);
    padding-top: 12px;
    padding-bottom: 12px;
  }
  tfoot tr.total .totals-value { color: var(--brand); }

  .empty { color: var(--text-secondary); text-align: center; padding: 28px 12px; font-style: italic; }

  /* ---- Notes ---- */
  .notes {
    margin-top: 18px;
    padding: 12px 14px;
    background: var(--notes-bg);
    border: 1px solid var(--notes-border);
    border-radius: 10px;
    font-size: 11px;
    color: var(--notes-fg);
    line-height: 1.55;
  }

  /* ---- Footer ---- */
  .foot {
    margin-top: 26px;
    padding-top: 14px;
    border-top: 1px solid var(--border);
    color: var(--text-secondary);
    font-size: 10px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .foot .brandline { color: var(--brand); font-weight: 700; }

  /* ---- On-screen action bar (hidden on print) ---- */
  .actions {
    position: fixed;
    top: 14px;
    right: 14px;
    display: flex;
    gap: 8px;
    z-index: 50;
  }
  .actions button {
    background: var(--orange);
    color: #fff;
    border: none;
    padding: 9px 14px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 12px;
    cursor: pointer;
    box-shadow: 0 4px 14px -4px rgba(11, 42, 74, 0.35);
    font-family: inherit;
  }
  .actions button:hover { background: var(--orange-hover); }
  .actions button.secondary { background: var(--brand); }

  /* ---- Responsive on-screen preview (mobile / tablet) ---- */
  @media screen and (max-width: 820px) {
    html, body { overflow-x: hidden; }
    body { padding: 16px 14px 32px; max-width: 100%; font-size: 13px; overflow-wrap: break-word; }
    p, h1, h2, h3, h4 { overflow-wrap: anywhere; }
    .grid { grid-template-columns: 1fr; }
    .brand { gap: 10px; padding-bottom: 12px; margin-bottom: 18px; }
    .brand .meta { font-size: 10px; }
    .actions { top: 8px; right: 8px; }
    table { font-size: 11px; }
    thead th, tbody td, tfoot td { padding: 8px 8px; }
    .card .row { font-size: 11px; display: block; padding: 4px 0; }
    .card .row .k { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
    .card .row .v { text-align: left; word-break: break-word; display: block; }
  }
  @media screen and (max-width: 480px) {
    .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    table { min-width: 480px; }
  }
</style>
</head>
<body>
  <div class="actions noprint">
    <button type="button" onclick="window.print()">Print / opslaan als PDF</button>
    <button type="button" class="secondary" onclick="window.close()">Sluiten</button>
  </div>

  <header class="brand">
    ${logoTag}
    <div class="meta">
      <div><span class="badge">Bestellijst</span></div>
      <div style="margin-top:6px">${escapeHtml(siteUrl)}</div>
    </div>
  </header>

  <section class="intro">
    <h1>Camerasysteem bestellijst</h1>
    <p>Samengesteld via de IPC Configurator op de officiële kadasterkaart van Nederland (PDOK).</p>
  </section>

  <div class="grid">
    <div class="card">
      <h3>Projectgegevens</h3>
      <div class="row"><span class="k">Projectnummer</span><span class="v">${escapeHtml(meta.projectNumber || "—")}</span></div>
      <div class="row"><span class="k">Klant</span><span class="v">${escapeHtml(meta.customerName || "—")}</span></div>
      <div class="row"><span class="k">Datum</span><span class="v">${escapeHtml(dateLabel || "—")}</span></div>
      <div class="row"><span class="k">Locatie</span><span class="v">${escapeHtml(meta.address || "—")}</span></div>
    </div>
    <div class="card">
      <h3>Systeemspecificatie</h3>
      <div class="row"><span class="k">Aantal camera's</span><span class="v">${bom.numCams}</span></div>
      <div class="row"><span class="k">Cameratype</span><span class="v">${escapeHtml(bom.cam.name)}</span></div>
      <div class="row"><span class="k">Kabeltracé</span><span class="v">${Math.round(bom.drawnMeters)} m + 25 m reserve</span></div>
      <div class="row"><span class="k">Bewaartermijn</span><span class="v">${meta.days} dagen · ${meta.bufferPct}% buffer</span></div>
    </div>
  </div>

  <div class="section-title">
    <h2>Bestellijst</h2>
    <span class="count">${bom.lines.length} artikel${bom.lines.length === 1 ? "" : "en"}</span>
  </div>

  <div class="table-wrap"><table>
    <thead>
      <tr>
        <th>Artikel</th>
        <th class="num">Aantal</th>
        <th class="num">Stuksprijs</th>
        <th class="num">Totaal</th>
      </tr>
    </thead>
    <tbody>
      ${rows || `<tr><td colspan="4" class="empty">Nog geen artikelen — plaats minimaal 1 camera op de kaart.</td></tr>`}
    </tbody>
    <tfoot>
      <tr class="subtotal">
        <td colspan="3" class="totals-label">Subtotaal (excl. btw)</td>
        <td class="totals-value">${formatEuroLocal(subtotal)}</td>
      </tr>
      <tr>
        <td colspan="3" class="totals-label">BTW 21%</td>
        <td class="totals-value">${formatEuroLocal(vat)}</td>
      </tr>
      <tr class="total">
        <td colspan="3" class="totals-label">Totaal (incl. btw)</td>
        <td class="totals-value">${formatEuroLocal(total)}</td>
      </tr>
    </tfoot>
  </table></div>

  <div class="notes">
    <strong>Let op:</strong> deze bestellijst is een indicatieve calculatie op basis
    van het door jou getekende kabeltracé. Definitieve prijzen, levertijden en
    eventuele installatiekosten worden bevestigd in de offerte.
  </div>

  <div class="foot">
    <div><span class="brandline">Elvion Group Nederland</span> · ${escapeHtml(siteUrl)}</div>
    <div>Gegenereerd ${new Date().toLocaleString("nl-NL")}</div>
  </div>

  <script>setTimeout(function(){ try { window.focus(); } catch(e){} }, 50);</script>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function openOrderPdfWindow(html: string) {
  if (typeof window === "undefined") return;
  const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=1100");
  if (!w) {
    toast.error("Sta pop-ups toe om de PDF te openen.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export const Route = createFileRoute("/cam-configurator")({
  head: () => {
    const url = `${SITE_URL}/cam-configurator`;
    const title = "IPC Configurator — Camerasysteem op kadasterkaart | Elvion Group Nederland";
    const description =
      "Stel je camerasysteem samen op een echte kadasterkaart: plaats camera's, teken kabels op schaal, wij berekenen kabel, connectoren, NVR en HDD.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: CamConfiguratorPage,
});

type Tool = "none" | "camera" | "cable";
type CamPoint = { id: string; lat: number; lng: number };
type CableLine = { id: string; points: { lat: number; lng: number }[] };

function CamConfiguratorPage() {
  const navigate = useNavigate();
  const [selectedCam, setSelectedCam] = useState<CamOption>(CAMERAS[1]);
  const [days, setDays] = useState(14);
  const [bufferPct, setBufferPct] = useState(20);
  const [tool, setTool] = useState<Tool>("none");
  const STORAGE_KEY = "cam-configurator:v1";
  type StoredState = {
    cams?: CamPoint[];
    lines?: CableLine[];
    projectNumber?: string;
    customerName?: string;
    projectDate?: string;
    address?: string;
  };
  const readStored = (): StoredState => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredState) : {};
    } catch {
      return {};
    }
  };
  const stored = typeof window !== "undefined" ? readStored() : {};
  const [cams, setCams] = useState<CamPoint[]>(
    Array.isArray(stored.cams) ? stored.cams : [],
  );
  const [lines, setLines] = useState<CableLine[]>(
    Array.isArray(stored.lines) ? stored.lines : [],
  );
  const [projectNumber, setProjectNumber] = useState<string>(
    stored.projectNumber ?? "",
  );
  const [customerName, setCustomerName] = useState<string>(
    stored.customerName ?? "",
  );
  const [projectDate, setProjectDate] = useState<string>(
    stored.projectDate ?? new Date().toISOString().slice(0, 10),
  );
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [currentConfigId, setCurrentConfigId] = useState<string | null>(null);
  const [savedConfigs, setSavedConfigs] = useState<
    Array<{
      id: string;
      project_number: string | null;
      customer_name: string | null;
      project_date: string;
      updated_at: string;
    }>
  >([]);
  const [savingRemote, setSavingRemote] = useState(false);
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(
    stored.address ?? "SugarCity Plaza 42, 1165PA Halfweg",
  );

  const [address, setAddress] = useState(
    stored.address ?? "SugarCity Plaza 42, 1165PA Halfweg",
  );
  const [suggestions, setSuggestions] = useState<{ id: string; label: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const addressInputRef = useRef<HTMLInputElement | null>(null);

  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const LRef = useRef<any>(null);
  const camLayerRef = useRef<any>(null);
  const cableLayerRef = useRef<any>(null);
  const addressMarkerRef = useRef<any>(null);
  const toolRef = useRef<Tool>("camera");
  const activeLineRef = useRef<string | null>(null);
  // Persistent dedupe registry — every id ever created/seen this session.
  const seenLineIdsRef = useRef<Set<string>>(new Set(lines.map((l) => l.id)));
  const seenCamIdsRef = useRef<Set<string>>(new Set(cams.map((c) => c.id)));

  // Auto-save to localStorage (debounced) so reloads keep state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handle = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            cams,
            lines,
            projectNumber,
            customerName,
            projectDate,
            address: selectedAddress,
          }),
        );
        setLastSavedAt(new Date());
      } catch {
        // ignore quota errors
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [cams, lines, projectNumber, customerName, projectDate, selectedAddress]);


  // Track auth state and load this user's saved configurations.
  useEffect(() => {
    let active = true;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setUserId(data.session?.user?.id ?? null);
      const sub = supabase.auth.onAuthStateChange((_e, session) => {
        setUserId(session?.user?.id ?? null);
      });
      (window as any).__camAuthSub = sub.data.subscription;
    })();
    return () => {
      active = false;
      try {
        (window as any).__camAuthSub?.unsubscribe?.();
      } catch {
        // ignore
      }
    };
  }, []);

  const refreshSavedConfigs = async () => {
    if (!userId) {
      setSavedConfigs([]);
      return;
    }
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase
      .from("cam_configurations")
      .select("id, project_number, customer_name, project_date, updated_at")
      .order("updated_at", { ascending: false })
      .limit(50);
    if (!error && data) setSavedConfigs(data as any);
  };

  useEffect(() => {
    refreshSavedConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const saveToAccount = async () => {
    if (!userId) {
      toast.error("Log in om in je account op te slaan");
      return;
    }
    setSavingRemote(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const payload = {
        user_id: userId,
        project_number: projectNumber || null,
        customer_name: customerName || null,
        project_date: projectDate,
        address: selectedAddress,
        selected_cam_id: selectedCam.slug,
        days,
        buffer_pct: bufferPct,
        cams: cams as any,
        lines: lines as any,
      };
      if (currentConfigId) {
        const { error } = await supabase
          .from("cam_configurations")
          .update(payload)
          .eq("id", currentConfigId);
        if (error) throw error;
        toast.success("Configuratie bijgewerkt in je account");
      } else {
        const { data, error } = await supabase
          .from("cam_configurations")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        setCurrentConfigId(data.id);
        toast.success("Configuratie opgeslagen in je account");
      }
      await refreshSavedConfigs();
    } catch (err: any) {
      toast.error(err?.message ?? "Opslaan mislukt");
    } finally {
      setSavingRemote(false);
    }
  };

  const loadFromAccount = async (id: string) => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase
      .from("cam_configurations")
      .select("*")
      .eq("id", id)
      .single();
    if (error || !data) {
      toast.error("Laden mislukt");
      return;
    }
    setCurrentConfigId(data.id);
    setProjectNumber(data.project_number ?? "");
    setCustomerName(data.customer_name ?? "");
    setProjectDate(data.project_date ?? new Date().toISOString().slice(0, 10));
    if (data.address) {
      setAddress(data.address);
      setSelectedAddress(data.address);
    }
    if (data.days != null) setDays(data.days);
    if (data.buffer_pct != null) setBufferPct(data.buffer_pct);
    if (data.selected_cam_id) {
      const cam = CAMERAS.find((c) => c.slug === data.selected_cam_id);
      if (cam) setSelectedCam(cam);
    }
    const loadedCams = Array.isArray(data.cams) ? (data.cams as any) : [];
    const loadedLines = Array.isArray(data.lines) ? (data.lines as any) : [];
    setCams(loadedCams);
    setLines(loadedLines);
    seenCamIdsRef.current = new Set(loadedCams.map((c: CamPoint) => c.id));
    seenLineIdsRef.current = new Set(loadedLines.map((l: CableLine) => l.id));
    setActiveLineId(null);
    activeLineRef.current = null;
    toast.success("Configuratie geladen");
  };

  const deleteFromAccount = async (id: string) => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase
      .from("cam_configurations")
      .delete()
      .eq("id", id);
    if (error) {
      toast.error("Verwijderen mislukt");
      return;
    }
    if (currentConfigId === id) setCurrentConfigId(null);
    await refreshSavedConfigs();
    toast.success("Configuratie verwijderd");
  };

  const newConfig = () => {
    setCurrentConfigId(null);
    setProjectNumber("");
    setCustomerName("");
    setProjectDate(new Date().toISOString().slice(0, 10));
    toast.info("Nieuwe configuratie gestart (lokaal)");
  };

  const getPdfBranding = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    let logoUrl: string | undefined;
    try {
      const mod: any = await import("@/assets/elvion-logo.png.asset.json");
      const rel: string | undefined = mod.default?.url ?? mod.url;
      if (rel) logoUrl = rel.startsWith("http") ? rel : `${origin}${rel}`;
    } catch {
      // logo optional — header falls back to text mark
    }
    return { origin, logoUrl };
  };

  const printOrderPdf = async () => {
    if (cams.length === 0) {
      toast.error("Plaats eerst minimaal 1 camera op de kaart.");
      return;
    }
    const bom = computeBom({
      cams,
      lines,
      selectedCamSlug: selectedCam.slug,
      days,
      bufferPct,
    });
    const branding = await getPdfBranding();
    openOrderPdfWindow(
      buildOrderPdfHtml(
        {
          projectNumber,
          customerName,
          projectDate,
          address: selectedAddress,
          days,
          bufferPct,
          ...branding,
        },
        bom,
      ),
    );
  };

  const printSavedOrderPdf = async (id: string) => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase
        .from("cam_configurations")
        .select("*")
        .eq("id", id)
        .single();
      if (error || !data) {
        toast.error("Laden mislukt");
        return;
      }
      const bom = computeBom({
        cams: Array.isArray(data.cams) ? (data.cams as any) : [],
        lines: Array.isArray(data.lines) ? (data.lines as any) : [],
        selectedCamSlug: data.selected_cam_id ?? selectedCam.slug,
        days: data.days ?? 14,
        bufferPct: data.buffer_pct ?? 20,
      });
      const branding = await getPdfBranding();
      openOrderPdfWindow(
        buildOrderPdfHtml(
          {
            projectNumber: data.project_number ?? "",
            customerName: data.customer_name ?? "",
            projectDate: data.project_date ?? new Date().toISOString().slice(0, 10),
            address: data.address ?? null,
            days: data.days ?? 14,
            bufferPct: data.buffer_pct ?? 20,
            ...branding,
          },
          bom,
        ),
      );
    } catch (err: any) {
      toast.error(err?.message ?? "PDF genereren mislukt");
    }
  };

  useEffect(() => {
    toolRef.current = tool;
  }, [tool]);
  useEffect(() => {
    activeLineRef.current = activeLineId;
  }, [activeLineId]);



  // Init Leaflet client-side
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof window === "undefined") return;
      const [L] = await Promise.all([
        import("leaflet"),
        import("leaflet/dist/leaflet.css"),
      ]);
      if (cancelled || !mapDivRef.current || mapRef.current) return;
      LRef.current = L;

      const map = L.map(mapDivRef.current, {
        center: [52.38532499, 4.74043699],
        zoom: 19,
        zoomControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        touchZoom: false,
      });

      L.tileLayer(
        "https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg",
        {
          attribution:
            '&copy; <a href="https://www.pdok.nl">PDOK</a> / Kadaster',
          maxZoom: 19,
          minZoom: 6,
        },
      ).addTo(map);

      camLayerRef.current = L.layerGroup().addTo(map);
      cableLayerRef.current = L.layerGroup().addTo(map);

      // Add default address marker
      const defaultLabel = "SugarCity Plaza 42, 1165PA Halfweg";
      const defaultLat = 52.38532499;
      const defaultLng = 4.74043699;
      const icon = L.divIcon({
        className: "address-pin",
        html: `<div style="position:relative;pointer-events:none;transform:translate(-50%,-100%);">
          <div style="background:hsl(var(--primary));color:hsl(var(--primary-foreground));font-size:11px;font-weight:600;padding:4px 8px;border-radius:6px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.25);max-width:240px;overflow:hidden;text-overflow:ellipsis;">${defaultLabel.replace(/</g, "&lt;")}</div>
          <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid hsl(var(--primary));margin:0 auto;"></div>
          <div style="width:14px;height:14px;border-radius:50%;background:hsl(var(--primary));border:3px solid white;box-shadow:0 0 0 2px hsl(var(--primary));margin:-4px auto 0;"></div>
        </div>`,
        iconSize: [240, 60],
        iconAnchor: [120, 60],
      });
      addressMarkerRef.current = L.marker([defaultLat, defaultLng], {
        icon,
        interactive: false,
        keyboard: false,
        zIndexOffset: 1000,
      }).addTo(map);

      map.on("click", (e: any) => {
        if (toolRef.current === "none") {
          toast.info("Selecteer eerst een tool: Camera plaatsen of Kabel tekenen");
          return;
        }
        const { lat, lng } = e.latlng;
        if (toolRef.current === "camera") {
          const id = `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          if (seenCamIdsRef.current.has(id)) return;
          seenCamIdsRef.current.add(id);
          setCams((prev) =>
            prev.some((c) => c.id === id) ? prev : [...prev, { id, lat, lng }],
          );
        } else {
          const activeId = activeLineRef.current;
          if (!activeId) {
            const id = `l-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            if (seenLineIdsRef.current.has(id)) return;
            seenLineIdsRef.current.add(id);
            activeLineRef.current = id;
            setActiveLineId(id);
            setLines((prev) =>
              prev.some((l) => l.id === id)
                ? prev
                : [...prev, { id, points: [{ lat, lng }] }],
            );

          } else {
            setLines((prev) => {
              if (!prev.some((l) => l.id === activeId)) {
                // Active line was lost (e.g. StrictMode re-run) — recreate it.
                return [...prev, { id: activeId, points: [{ lat, lng }] }];
              }
              return prev.map((l) => {
                if (l.id !== activeId) return l;
                const last = l.points[l.points.length - 1];
                if (last && last.lat === lat && last.lng === lng) return l;
                return { ...l, points: [...l.points, { lat, lng }] };
              });
            });
          }

        }
      });

      mapRef.current = map;

      // Ensure map fills its container (fixes grey area when container resizes after init)
      const invalidate = () => map.invalidateSize();
      requestAnimationFrame(invalidate);
      setTimeout(invalidate, 200);
      setTimeout(invalidate, 600);
      const ro = new ResizeObserver(invalidate);
      ro.observe(mapDivRef.current);
      (map as any)._roCleanup = () => ro.disconnect();
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) {
        (mapRef.current as any)._roCleanup?.();
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Render cameras on the map
  useEffect(() => {
    const L = LRef.current;
    const layer = camLayerRef.current;
    if (!L || !layer) return;
    layer.clearLayers();
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:26px;height:26px;border-radius:50%;background:hsl(24 95% 53%);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:12px;">C</div>`,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
    cams.forEach((c, idx) => {
      L.marker([c.lat, c.lng], { icon })
        .addTo(layer)
        .bindTooltip(`Camera ${idx + 1}`, { direction: "top" });
    });
  }, [cams]);

  // Render cables on the map
  useEffect(() => {
    const L = LRef.current;
    const layer = cableLayerRef.current;
    if (!L || !layer) return;
    layer.clearLayers();
    lines.forEach((line) => {
      if (line.points.length >= 2) {
        L.polyline(
          line.points.map((p) => [p.lat, p.lng]),
          { color: "hsl(24 95% 53%)", weight: 4, opacity: 0.9 },
        ).addTo(layer);
      }
      line.points.forEach((p) => {
        L.circleMarker([p.lat, p.lng], {
          radius: 3,
          color: "hsl(24 95% 53%)",
          fillColor: "hsl(24 95% 53%)",
          fillOpacity: 1,
        }).addTo(layer);
      });
    });
  }, [lines]);

  // Meters via Haversine (Leaflet's distanceTo)
  const drawnMeters = useMemo(() => {
    const L = LRef.current;
    if (!L) return 0;
    let total = 0;
    for (const line of lines) {
      for (let i = 1; i < line.points.length; i++) {
        const a = L.latLng(line.points[i - 1].lat, line.points[i - 1].lng);
        const b = L.latLng(line.points[i].lat, line.points[i].lng);
        total += a.distanceTo(b);
      }
    }
    return total;
  }, [lines]);

  const numCams = cams.length;
  const cableMeters = Math.max(0, Math.round(drawnMeters + (drawnMeters > 0 ? 25 : 0)));
  const rj45Needed = numCams * 3;
  const rj45Packs = Math.ceil(rj45Needed / RJ45.perPack);
  const nvr = nvrFor(Math.max(1, numCams));

  // HDD via bitrate
  const hdd = useMemo(() => {
    const cnt = Math.max(1, numCams);
    const bitrate = BITRATE_MBPS[selectedCam.mp] ?? 4;
    const gbPerCamPerDay = (bitrate * 86400) / 8 / 1024;
    const totalGb = gbPerCamPerDay * cnt * days * (1 + bufferPct / 100);
    const totalTb = totalGb / 1024;
    const recommended = HDDS.find((h) => h.capacityTb >= totalTb) ?? HDDS[HDDS.length - 1];
    return { recommended, neededTb: totalTb };
  }, [numCams, selectedCam, days, bufferPct]);

  const totalCents = useMemo(() => {
    if (numCams === 0) return 0;
    return (
      numCams * selectedCam.priceCents +
      numCams * BRACKET.priceCents +
      cableMeters * CABLE.priceCents +
      rj45Packs * RJ45.priceCents +
      nvr.priceCents +
      hdd.recommended.priceCents
    );
  }, [numCams, selectedCam, cableMeters, rj45Packs, nvr, hdd]);

  // Address geocoding via PDOK Locatieserver
  useEffect(() => {
    if (!address || address.length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await fetch(
          `https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?q=${encodeURIComponent(
            address,
          )}&fq=type:adres&rows=6`,
        );
        const json = await res.json();
        const docs = json?.response?.docs ?? [];
        setSuggestions(
          docs.map((d: any) => ({ id: d.id, label: d.weergavenaam })),
        );
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [address]);

  const flyToSuggestion = async (id: string, label: string) => {
    try {
      const res = await fetch(
        `https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup?id=${encodeURIComponent(id)}`,
      );
      const json = await res.json();
      const doc = json?.response?.docs?.[0];
      if (!doc) return;
      const m = /POINT\(([-\d.]+)\s+([-\d.]+)\)/.exec(doc.centroide_ll);
      if (!m) return;
      const lng = Number(m[1]);
      const lat = Number(m[2]);
      const map = mapRef.current;
      const L = LRef.current;

      if (L && map) {
        const icon = L.divIcon({
          className: "address-pin",
          html: `<div style="position:relative;pointer-events:none;transform:translate(-50%,-100%);">
            <div style="background:hsl(var(--primary));color:hsl(var(--primary-foreground));font-size:11px;font-weight:600;padding:4px 8px;border-radius:6px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.25);max-width:240px;overflow:hidden;text-overflow:ellipsis;">${label.replace(/</g, "&lt;")}</div>
            <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid hsl(var(--primary));margin:0 auto;"></div>
            <div style="width:14px;height:14px;border-radius:50%;background:hsl(var(--primary));border:3px solid white;box-shadow:0 0 0 2px hsl(var(--primary));margin:-4px auto 0;"></div>
          </div>`,
          iconSize: [240, 60],
          iconAnchor: [120, 60],
        });
        if (addressMarkerRef.current) {
          map.removeLayer(addressMarkerRef.current);
        }
        addressMarkerRef.current = L.marker([lat, lng], {
          icon,
          interactive: false,
          keyboard: false,
          zIndexOffset: 1000,
        }).addTo(map);

        map.setView([lat, lng], 19, { animate: false });
      }

      setAddress(label);
      setSelectedAddress(label);
      setSuggestions([]);
    } catch {
      // ignore
    }
  };

  const changeAddress = () => {
    if (addressMarkerRef.current && mapRef.current) {
      mapRef.current.removeLayer(addressMarkerRef.current);
      addressMarkerRef.current = null;
    }
    setSelectedAddress(null);
    setAddress("");
    addressInputRef.current?.focus();
  };

  const undoLastCable = () =>
    setLines((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.points.length <= 1) return prev.slice(0, -1);
      return [
        ...prev.slice(0, -1),
        { ...last, points: last.points.slice(0, -1) },
      ];
    });

  const finishLine = () => {
    setActiveLineId(null);
    activeLineRef.current = null;
  };

  const clearAll = () => {
    setCams([]);
    setLines([]);
    setActiveLineId(null);
    activeLineRef.current = null;
    seenLineIdsRef.current.clear();
    seenCamIdsRef.current.clear();
  };


  const requestQuote = () => {
    if (numCams === 0) {
      toast.error("Plaats eerst minimaal 1 camera op de kaart.");
      return;
    }
    navigate({ to: "/offerte-aanvragen", search: { product: "camerabewaking" } });
  };

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          IPC Configurator: stel je camerasysteem samen op de kadasterkaart
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Voer je adres in, plaats je camera's en teken het kabeltracé direct op
          de <strong>officiële kadasterkaart van Nederland (PDOK)</strong>. Wij
          rekenen automatisch het aantal camera's, meters CAT6a-kabel,
          RJ45-connectoren, montagebeugels, een passende NVR en de juiste
          surveillance-grade harde schijf uit. Vraag daarna eenvoudig een
          offerte aan.

        </p>

        {/* Tip-block over megapixels */}
        <section className="mt-8 grid gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-4 sm:p-6">
          {[
            { mp: "2MP", use: "Hal, voordeur, kleine ruimtes", detail: "Overzicht tot ±10 m" },
            { mp: "4MP", use: "Oprit, gevel, winkel", detail: "Gezichtsherkenning ±15 m" },
            { mp: "6MP", use: "Parkeerterrein, opslag", detail: "Detail tot ±25 m" },
            { mp: "8MP/4K", use: "Grote terreinen, forensisch", detail: "Inzoomen zonder verlies" },
          ].map((b) => (
            <div key={b.mp} className="rounded-xl bg-background p-4 ring-1 ring-border">
              <p className="text-xs font-semibold uppercase tracking-wider text-orange">
                {b.mp}
              </p>
              <p className="mt-1 text-sm font-semibold text-foreground">{b.use}</p>
              <p className="mt-0.5 text-xs text-text-secondary">{b.detail}</p>
            </div>
          ))}
        </section>

        {/* Projectgegevens + auto-save */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">Projectgegevens</h2>
              <p className="text-xs text-text-secondary">
                Auto-save in deze browser
                {lastSavedAt && (
                  <> · laatst opgeslagen {lastSavedAt.toLocaleTimeString("nl-NL")}</>
                )}
                {currentConfigId && <> · gekoppeld aan account</>}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={printOrderPdf}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:border-orange"
              >
                Bestellijst (PDF)
              </button>
              {userId ? (
                <>
                  <button
                    type="button"
                    onClick={saveToAccount}
                    disabled={savingRemote}
                    className="inline-flex items-center gap-1.5 rounded-md bg-orange px-3 py-2 text-xs font-semibold text-white hover:bg-orange/90 disabled:opacity-60"
                  >
                    {savingRemote
                      ? "Opslaan…"
                      : currentConfigId
                        ? "Bijwerken in account"
                        : "Opslaan in account"}
                  </button>
                  {currentConfigId && (
                    <button
                      type="button"
                      onClick={newConfig}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:border-orange"
                    >
                      Nieuwe configuratie
                    </button>
                  )}
                </>
              ) : (
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-1.5 rounded-md border border-orange/40 bg-orange/10 px-3 py-2 text-xs font-semibold text-orange hover:bg-orange/20"
                >
                  Inloggen om in account op te slaan
                </Link>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-xs font-semibold text-foreground">Projectnummer</label>
              <input
                type="text"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value.slice(0, 50))}
                placeholder="bijv. P-2026-014"
                maxLength={50}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Klantnaam</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value.slice(0, 120))}
                placeholder="bijv. Bakkerij Jansen B.V."
                maxLength={120}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Datum</label>
              <input
                type="date"
                value={projectDate}
                onChange={(e) => setProjectDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
              />
            </div>
          </div>

          {userId && savedConfigs.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-semibold text-foreground">Opgeslagen in je account</p>
              <ul className="mt-2 divide-y divide-border rounded-md border border-border">
                {savedConfigs.map((c) => (
                  <li
                    key={c.id}
                    className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm ${
                      currentConfigId === c.id ? "bg-orange/5" : ""
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">
                        {c.project_number || "(zonder projectnummer)"}
                        {c.customer_name && (
                          <span className="text-text-secondary"> · {c.customer_name}</span>
                        )}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {new Date(c.project_date).toLocaleDateString("nl-NL")} · bijgewerkt{" "}
                        {new Date(c.updated_at).toLocaleString("nl-NL")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => loadFromAccount(c.id)}
                        className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold hover:border-orange"
                      >
                        Laden
                      </button>
                      <button
                        type="button"
                        onClick={() => printSavedOrderPdf(c.id)}
                        className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold hover:border-orange"
                      >
                        PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm("Verwijderen?")) deleteFromAccount(c.id);
                        }}
                        className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold text-destructive hover:border-destructive"
                      >
                        Verwijderen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Adres + tools */}

        <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <label className="text-sm font-semibold text-foreground">
                Adres (NL)
              </label>
              <div className="relative mt-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-secondary" />
                <input
                  ref={addressInputRef}
                  type="text"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    if (selectedAddress) setSelectedAddress(null);
                  }}
                  placeholder="Bijv. Hoofdstraat 1, Utrecht"
                  className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm focus:border-orange focus:outline-none"
                />
                {suggestions.length > 0 && (
                  <ul className="absolute z-[1000] mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-background shadow-lg">
                    {suggestions.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => flyToSuggestion(s.id, s.label)}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                        >
                          {s.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {searching && (
                  <p className="mt-1 text-xs text-text-secondary">Zoeken…</p>
                )}
              </div>
              {selectedAddress && (
                <div className="mt-3 flex items-center justify-between rounded-lg border border-orange/30 bg-orange/10 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-orange" />
                    <span className="text-sm font-semibold text-foreground">
                      {selectedAddress}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={changeAddress}
                    className="text-sm font-medium text-orange underline hover:text-orange/80"
                  >
                    Wijzigen
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-end gap-2">
              <button
                type="button"
                onClick={() => setTool("none")}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  tool === "none"
                    ? "border-orange bg-orange text-white"
                    : "border-border bg-background text-foreground hover:border-orange"
                }`}
              >
                <MousePointer className="size-4" />
                Bekijken
              </button>
              <button
                type="button"
                onClick={() => setTool("camera")}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  tool === "camera"
                    ? "border-orange bg-orange text-white"
                    : "border-border bg-background text-foreground hover:border-orange"
                }`}
              >
                <Camera className="size-4" />
                Camera plaatsen
              </button>
              <button
                type="button"
                onClick={() => setTool("cable")}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  tool === "cable"
                    ? "border-orange bg-orange text-white"
                    : "border-border bg-background text-foreground hover:border-orange"
                }`}
              >
                <Pencil className="size-4" />
                Kabel tekenen
              </button>
              {tool === "cable" && (
                <>
                  <button
                    type="button"
                    onClick={finishLine}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:border-orange"
                  >
                    Lijn afsluiten
                  </button>
                  <button
                    type="button"
                    onClick={undoLastCable}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:border-orange"
                  >
                    <Eraser className="size-3.5" />
                    Punt terug
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground hover:border-orange"
              >
                <Trash2 className="size-3.5" />
                Alles wissen
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-text-secondary">
            <MapPin className="mr-1 inline size-3.5 text-orange" />
            Tip: zoek je adres en zoom in. Kies eerst een tool (Camera plaatsen of Kabel tekenen) en klik dan op de kaart.
            Onderlaag: actuele luchtfoto PDOK, op schaal.
          </p>
        </section>

        {/* Kaart + Resultaat */}
        <section className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="flex min-h-[520px] flex-col overflow-hidden rounded-2xl border border-border shadow-sm">
            <div
              ref={mapDivRef}
              className="min-h-[520px] w-full flex-1 bg-muted"
              role="application"
              aria-label="Kadasterkaart configurator"
            />
          </div>

          <aside className="rounded-2xl border border-orange/30 bg-gradient-to-br from-orange/5 to-transparent p-6 shadow-sm sm:p-7">
            <div className="mb-4 flex items-center gap-2 text-orange">
              <Calculator className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Jouw configuratie
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Camera type
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {CAMERAS.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => setSelectedCam(c)}
                      className={`rounded-md border px-2 py-2 text-left text-xs transition-colors ${
                        selectedCam.slug === c.slug
                          ? "border-orange bg-orange/10"
                          : "border-border bg-background hover:border-orange"
                      }`}
                    >
                      <span className="block font-semibold text-foreground">
                        {c.mp}MP
                      </span>
                      <span className="block text-text-secondary">
                        {formatEuro(c.priceCents)}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-text-secondary">{selectedCam.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center justify-between text-xs font-semibold">
                    Bewaartermijn
                    <span className="rounded bg-muted px-1.5 text-xs font-bold">{days}d</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={60}
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="mt-2 w-full accent-orange"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-between text-xs font-semibold">
                    Reserve
                    <span className="rounded bg-muted px-1.5 text-xs font-bold">+{bufferPct}%</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={5}
                    value={bufferPct}
                    onChange={(e) => setBufferPct(Number(e.target.value))}
                    className="mt-2 w-full accent-orange"
                  />
                </div>
              </div>

              <div className="divide-y divide-border rounded-xl border border-border bg-background">
                <Row
                  icon={<Camera className="size-4" />}
                  label={`Camera's (${selectedCam.mp}MP)`}
                  qty={numCams}
                  priceCents={numCams * selectedCam.priceCents}
                />
                <Row
                  icon={<MapPin className="size-4" />}
                  label="Muurbeugels"
                  qty={numCams}
                  priceCents={numCams * BRACKET.priceCents}
                />
                <Row
                  icon={<Cable className="size-4" />}
                  label={`CAT6a kabel (${Math.round(drawnMeters)}m + 25m reserve)`}
                  qty={`${cableMeters} m`}
                  priceCents={cableMeters * CABLE.priceCents}
                />
                <Row
                  icon={<Plug className="size-4" />}
                  label={`RJ45 keystone (${rj45Needed}× nodig · 10/pak)`}
                  qty={`${rj45Packs} pak`}
                  priceCents={rj45Packs * RJ45.priceCents}
                />
                <Row
                  icon={<Server className="size-4" />}
                  label={nvr.name}
                  qty={1}
                  priceCents={nvr.priceCents}
                />
                <Row
                  icon={<HardDrive className="size-4" />}
                  label={`${hdd.recommended.name} (${hdd.neededTb.toFixed(2)} TB nodig)`}
                  qty={1}
                  priceCents={hdd.recommended.priceCents}
                  inStock={hdd.recommended.inStock}
                />
              </div>

              <div className="flex items-baseline justify-between border-t border-border pt-3">
                <span className="text-sm font-semibold text-foreground">Totaal</span>
                <span className="text-2xl font-bold text-orange">
                  {formatEuro(totalCents)}
                  <span className="ml-1 text-xs font-medium text-text-secondary">
                    excl. btw
                  </span>
                </span>
              </div>

              <button
                type="button"
                onClick={requestQuote}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-orange px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
              >
                Offerte aanvragen
              </button>
              <Link
                to="/hd-configurator"
                className="block text-center text-xs font-semibold text-orange hover:underline"
              >
                Liever alleen opslag berekenen? → HD Calculator
              </Link>
            </div>
          </aside>
        </section>

        {/* Hoe het werkt */}
        <section className="mt-12 grid gap-8 lg:grid-cols-3">
          {[
            {
              t: "1. Adres invoeren",
              d: "Vul je adres in en de kadasterkaart zoomt direct naar jouw locatie op schaal.",
            },
            {
              t: "2. Camera's plaatsen",
              d: "Kies 'Camera plaatsen' en klik op de hoeken van je pand of paaltjes waar je een camera wilt.",
            },
            {
              t: "3. Kabeltracé tekenen",
              d: "Kies 'Kabel tekenen' en klik het pad van iedere camera naar je NVR. Wij tellen de meters + 25m reserve.",
            },
          ].map((s) => (
            <div key={s.t} className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-base font-bold text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm text-text-secondary">{s.d}</p>
            </div>
          ))}
        </section>

        {/* Voordelen */}
        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">Waarom deze configurator?</h2>
            <ul className="mt-3 space-y-2">
              {[
                "Echte kadasterondergrond (PDOK BRT) — geen ruwe schatting",
                "Realistische kabelmeters met automatische 25m reserve",
                "RJ45-pakketten worden naar boven afgerond op 10 stuks",
                "NVR-keuze schaalt automatisch mee (4/8/16/32-kanaals)",
                "HDD-berekening gelijk aan onze HD Calculator",
                "Eén klik: complete set als offerteaanvraag",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Tips voor een goed plan</h2>
            <ul className="mt-3 space-y-2">
              {[
                "Plaats camera's altijd hoog (3–3,5 m) en uit handbereik",
                "Vermijd tegenlicht (camera niet pal richting de zon)",
                "Reken minimaal 1 camera per kritieke zichtlijn",
                "Houd kabeltrajecten kort waar mogelijk (<90m per run)",
                "Plan een POE-switch bij meer dan 8 camera's of grote afstanden",
                "Combineer 4MP-dome's met een PTZ voor groot terrein",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">Veelgestelde vragen</h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="p-5">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between text-left font-semibold text-foreground"
                  >
                    {f.q}
                    <span
                      className={`ml-3 text-orange transition-transform ${isOpen ? "rotate-180" : ""}`}
                    >
                      ▼
                    </span>
                  </button>
                  {isOpen && (
                    <p className="mt-2 text-text-secondary">{f.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">Hulp bij je configuratie?</h2>
          <p className="mt-2 text-text-secondary">
            Onze adviseurs kijken graag mee. We helpen je het juiste aantal camera's,
            de beste posities en een kloppend kabeltracé bepalen — inclusief installatie.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/info/$slug"
              params={{ slug: "beveiliging" }}
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Meer over beveiliging
            </Link>
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Row({
  icon,
  label,
  qty,
  priceCents,
  inStock,
}: {
  icon: React.ReactNode;
  label: string;
  qty: number | string;
  priceCents: number;
  inStock?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 text-sm">
      <span className="text-orange">{icon}</span>
      <span className="flex-1 text-foreground">
        {label}
        {inStock === false && (
          <span className="ml-2 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary">
            Niet op voorraad
          </span>
        )}
      </span>
      <span className="w-16 text-right text-text-secondary">{qty}</span>
      <span className="w-24 text-right font-semibold text-foreground">
        {formatEuro(priceCents)}
      </span>
    </div>
  );
}
